import {
  getShirtDetails as getShirtDetailsCall,
  searchShirts as searchShirtsCall,
  searchShirtsPublic as searchShirtsPublicCall,
  newShirt as newShirtCall,
  deleteShirt as deleteShirtCall,
  updateProperty as updatePropertyCall,
  updateImages as updateImagesCall,
  updateShirt as updateShirtCall,
  getTop10 as getTop10Call,
  addItemTop10 as addItemTop10Call,
  deleteItemTop10 as deleteItemTop10Call,
  searchShopShirts as searchShopShirtsCall,
} from "../api/shirtService";
import LoginService from "../loginService";
import NotificationService from "../../components/Notification/notificationService";
import { Shirt } from "../../entities/Shirt";
import {
  ShirtInterface,
  NewShirt,
  Property,
  Image,
  SearchShirtResponse,
  TopItem,
  ShopShirtInterface,
  SearchShopShirtResponse,
  TopItemInterface,
} from "./types";
import {
  Operations,
  ParamData,
  ParamsData,
  buildParams,
  buildParamsData,
  buildParamsDataOld,
} from "../api/api";
import { CustomParam, Dictionary } from "../../types/types";

export default function ShirtsService() {
  const { error: errorNotification } = NotificationService();
  const { setLogout } = LoginService();

  const handleError = (status: number, description: string) => {
    if (status === 401) {
      setLogout();
    } else {
      errorNotification(description);
    }
  };

  const getShirtDetails = async (shirtId: number) => {
    let response = await getShirtDetailsCall(shirtId).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });
    if (response?.data) {
      const data: ShirtInterface = response.data;
      const shirt = new Shirt(data);

      return shirt;
    } else {
      return undefined;
    }
  };

  const searchShirts = async (p: Dictionary<string | number>) => {
    let params: ParamsData = buildParamsDataOld(p);
    const paramsText = buildParams(params);
    let response = await searchShirtsCall(paramsText).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });
    if (response?.data) {
      const preResult: Array<ShirtInterface> = response.data;
      const shirtsResult: Array<Shirt> = [];
      preResult.forEach((u) => {
        shirtsResult.push(new Shirt(u));
      });

      const result: SearchShirtResponse = {
        shirts: shirtsResult,
        lastPage: response.meta.last_page,
        total: response.meta.total,
      };

      return result;
    } else {
      const result: SearchShirtResponse = {
        shirts: [],
        lastPage: undefined,
      };

      return result;
    }
  };

  const searchShirtsPublic = async (p: Dictionary<string | number>) => {
    let params: ParamsData = buildParamsDataOld(p);
    const paramsText = buildParams(params);
    let response = await searchShirtsPublicCall(paramsText).catch(
      function (error) {
        // For public endpoints, don't handle errors the same way as authenticated endpoints
        console.error("Public shirts search error:", error);
      }
    );
    if (response?.data) {
      const preResult: Array<ShirtInterface> = response.data;
      const shirtsResult: Array<Shirt> = [];
      preResult.forEach((u) => {
        shirtsResult.push(new Shirt(u));
      });

      const result: SearchShirtResponse = {
        shirts: shirtsResult,
        lastPage: response.meta.last_page,
        total: response.meta.total,
      };

      return result;
    } else {
      const result: SearchShirtResponse = {
        shirts: [],
        lastPage: undefined,
      };

      return result;
    }
  };

  const searchShopShirts = async (p: CustomParam<string | number>) => {
    let params: ParamsData = buildParamsData(p);
    const paramsText = buildParams(params);
    let response = await searchShopShirtsCall(paramsText).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );
    if (response?.data) {
      const shirtsResult: Array<ShopShirtInterface> = response.data;
      const result: SearchShopShirtResponse = {
        shirts: shirtsResult,
        lastPage: response.meta.last_page,
        total: response.meta.total,
      };

      return result;
    } else {
      const result: SearchShopShirtResponse = {
        shirts: [],
        lastPage: undefined,
        total: 0,
      };

      return result;
    }
  };

  const newShirt = async (shirt: NewShirt) => {
    let response = await newShirtCall(shirt).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    let shirtSize = shirt.size;

    if (response?.data) {
      const data: ShirtInterface = response.data;
      const shirt = new Shirt(data);

      if (shirt.id) {
        await updatePropertyCall(shirt.id, 7, shirtSize);
      }

      return shirt;
    } else {
      return undefined;
    }
  };

  const deleteShirt = async (shirtId: number) => {
    let response = await deleteShirtCall(shirtId).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response?.status === 204) {
      return true;
    } else {
      return false;
    }
  };

  const updateShirt = async (shirtId: number, shirt: NewShirt) => {
    let response = await updateShirtCall(shirtId, shirt).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );
    if (response?.data) {
      const data: ShirtInterface = response.data;
      const shirt = new Shirt(data);

      return shirt;
    } else {
      return undefined;
    }
  };

  const updateProperty = async (
    shirtId: number,
    propertyId: number,
    extraData: any,
    prevPropertyId?: number
  ) => {
    let response = await updatePropertyCall(
      shirtId,
      propertyId,
      extraData,
      prevPropertyId
    ).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });
    if (response?.data) {
      return true;
    } else {
      return false;
    }
  };

  const updateImages = async (shirtId: number, images: Array<Image>) => {
    let response = await updateImagesCall(shirtId, images).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );
    if (response?.data) {
      return true;
    } else {
      return false;
    }
  };

  const getTop10 = async (userId: number) => {
    let response = await getTop10Call(userId).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });
    if (response?.data) {
      const result: Array<TopItem> = [];
      response.data.forEach((element: TopItemInterface) => {
        result.push({
          position: element.position,
          item: new Shirt(element.item),
        });
      });
      return result;
    } else {
      return undefined;
    }
  };

  const addItemTop10 = async (shirtId: number, position: number) => {
    let response = await addItemTop10Call(shirtId, position).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );

    if (response?.status === 204) {
      return true;
    } else {
      return false;
    }
  };
  const deleteItemTop10 = async (shirtId: number) => {
    let response = await deleteItemTop10Call(shirtId).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response?.status === 204) {
      return true;
    } else {
      return false;
    }
  };

  return {
    getShirtDetails,
    searchShirts,
    searchShirtsPublic,
    newShirt,
    deleteShirt,
    updateProperty,
    updateImages,
    updateShirt,
    getTop10,
    addItemTop10,
    deleteItemTop10,
    searchShopShirts,
  };
}
